import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Link,
  Paper,
} from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { loginUi, forgotPasswordUi } from "../../../api/ui";
import Header from "../../../components/header";
import { useUiAuth } from "../../../context/UiAuthContext";

export function AssinaturasLogin(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUiAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Forgot password modal state
  const [openForgotModal, setOpenForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const locState = location.state as { from?: { pathname?: string } } | null;
  const from = locState?.from?.pathname || "/assinaturas";

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await loginUi(email, password);
      login(response.token, {
        name: response.name,
        email: response.email,
        role: response.role,
        apiKeyAnonymized: response.apiKeyAnonymized,
      });
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      const msg = errorObj.response?.data?.message;
      setError(typeof msg === "string" ? msg : t("assinaturas.login.invalidCredentialsError"));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForgotModal = (): void => {
    setForgotEmail(email);
    setForgotSuccess(null);
    setForgotError(null);
    setOpenForgotModal(true);
  };

  const handleSendForgotEmail = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setForgotSuccess(null);
    setForgotError(null);
    setForgotLoading(true);

    try {
      await forgotPasswordUi(forgotEmail);
      setForgotSuccess(t("assinaturas.login.forgotModalSuccess"));
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      const msg = errorObj.response?.data?.message;
      setForgotError(typeof msg === "string" ? msg : t("assinaturas.login.forgotModalError"));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f8fafc" }}>
      {/* Top Header igual ao layout da tela de assinar documento */}
      <Header />

      {/* Área central com o Card de Login */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 420,
            width: "100%",
            borderRadius: 3,
            p: 4,
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)",
            bgcolor: "#ffffff",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                bgcolor: "#eeebfc",
                color: "#2F16C9",
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography color="#1e293b" component="h1" fontWeight="bold" gutterBottom variant="h5">
              {t("assinaturas.login.title")}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t("assinaturas.login.subtitle")}
            </Typography>
          </Box>

          {error ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={(e) => { void handleSubmit(e); }}>
            <TextField
              autoComplete="email"
              fullWidth
              id="email"
              label={t("assinaturas.login.emailLabel")}
              margin="normal"
              name="email"
              onChange={(e) => { setEmail(e.target.value); }}
              required
              value={email}
            />

            <TextField
              autoComplete="current-password"
              fullWidth
              id="password"
              label={t("assinaturas.login.passwordLabel")}
              margin="normal"
              name="password"
              onChange={(e) => { setPassword(e.target.value); }}
              required
              type="password"
              value={password}
            />

            <Box sx={{ textAlign: "right", mt: 1, mb: 2.5 }}>
              <Link
                component="button"
                onClick={handleOpenForgotModal}
                sx={{ color: "#2F16C9", fontWeight: 500 }}
                type="button"
                underline="hover"
                variant="body2"
              >
                {t("assinaturas.login.forgotPasswordLink")}
              </Link>
            </Box>

            <Button
              disabled={loading}
              fullWidth
              sx={{
                py: 1.5,
                bgcolor: "#2F16C9",
                fontWeight: "bold",
                borderRadius: 2,
                boxShadow: "0 2px 4px 0 rgba(47, 22, 201, 0.2)",
                "&:hover": { bgcolor: "#0C1C69" },
              }}
              type="submit"
              variant="contained"
            >
              {loading ? <CircularProgress color="inherit" size={24} /> : t("assinaturas.login.submitButton")}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Modal Esqueci a Senha */}
      <Dialog fullWidth maxWidth="xs" onClose={() => { setOpenForgotModal(false); }} open={openForgotModal}>
        <DialogTitle sx={{ fontWeight: "bold" }}>{t("assinaturas.login.forgotModalTitle")}</DialogTitle>
        <Box component="form" onSubmit={(e) => { void handleSendForgotEmail(e); }}>
          <DialogContent>
            <Typography color="text.secondary" mb={2} variant="body2">
              {t("assinaturas.login.forgotModalDescription")}
            </Typography>

            {forgotSuccess ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {forgotSuccess}
              </Alert>
            ) : null}

            {forgotError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {forgotError}
              </Alert>
            ) : null}

            <TextField
              fullWidth
              label={t("assinaturas.login.yourEmailLabel")}
              margin="dense"
              onChange={(e) => { setForgotEmail(e.target.value); }}
              required
              type="email"
              value={forgotEmail}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button color="inherit" onClick={() => { setOpenForgotModal(false); }}>
              {t("assinaturas.login.closeButton")}
            </Button>
            <Button disabled={forgotLoading} sx={{ bgcolor: "#2F16C9", "&:hover": { bgcolor: "#0C1C69" } }} type="submit" variant="contained">
              {forgotLoading ? <CircularProgress color="inherit" size={20} /> : t("assinaturas.login.sendCodeButton")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AssinaturasLogin;
